import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import type { PaginatedResponse } from "@/shared/http";

const DEFAULT_PAGE_SIZE = 50;

export interface PaginatedQueryResult<T> {
  /**
   * The current page's items, or `undefined` when the page hasn't loaded
   * yet.
   *
   * May briefly be `undefined` during forward navigation if the consumer
   * outpaces the prefetch.
   */
  data: T[] | undefined;
  error: Error | null;
  status: "pending" | "error" | "success";
  /**
   * `true` only during the very first fetch (no data at all yet).
   */
  isLoading: boolean;
  /** Any fetch in flight: initial, prefetch, advance, refetch. */
  isFetching: boolean;
  /**
   * `true` if there's a page after `currentPage`, either already
   * loaded in `pages[currentPage + 1]` or fetchable from the server.
   */
  hasNextPage: boolean;
}

/**
 * Page-aware results hook for cursor-paginated queries displayed in a
 * page based pagination UI (page 1, 2, 3, …).
 *
 * For "load more" UIs that want all pages concatenated, use
 * `useInfiniteQueryResults` instead.
 */
export function usePaginatedQueryResults<T>(
  queryId: string | undefined,
  currentPage: number,
  pageSize: number = DEFAULT_PAGE_SIZE,
): PaginatedQueryResult<T> {
  const { queryService } = useDomainServices();
  const validPage = currentPage >= 0;

  /**
   * Using useInfiniteQuery internally because its API plays really
   * nicely with cursor based pagination while also getting other
   * tanstack query goodies like caching, loading, error states.
   */
  const q = useInfiniteQuery({
    queryKey: ["queryResults", queryId, pageSize],
    queryFn: ({ pageParam, signal }) =>
      queryService.getQueryResults<T>(queryId!, {
        abortSignal: signal,
        nextPageToken: pageParam,
        pageSize,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: PaginatedResponse<T>) =>
      last.next_token ?? undefined,
    enabled: !!queryId && validPage,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  React.useEffect(
    function prefetchNextPage() {
      /**
       * Short circuits if
       * 1. We don't have data
       * 2. A fetch is already in-flight
       * 3. There's no more to fetch
       */
      if (!q.data || q.isFetchingNextPage || q.hasNextPage === false) {
        return;
      }

      const loaded = q.data.pages.length;
      if (currentPage >= loaded - 1) {
        void q.fetchNextPage();
      }
    },
    [currentPage, q.data, q.hasNextPage, q.isFetchingNextPage, q],
  );

  if (!validPage) {
    return {
      data: undefined,
      error: new Error(`Invalid page index: ${currentPage}`),
      status: "error",
      isLoading: false,
      isFetching: false,
      hasNextPage: false,
    };
  }

  const loaded = q.data?.pages.length;

  // "Next is in memory" OR "more can be fetched after the tail."
  const hasNextPage =
    (loaded !== undefined && currentPage + 1 < loaded) || q.hasNextPage;

  return {
    data: q.data?.pages[currentPage]?.items,
    error: q.error,
    status: q.status,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    hasNextPage,
  };
}
